import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Grid,
  GridItem,
  Icon,
  Select,
  Tag,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { DeleteIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { MdAlternateEmail, MdChat, MdSms } from "react-icons/md";
import {
  CANAL_LABEL,
  CanalComunicacao,
  CatalogoDisparo,
  ContextoDisparo,
  DestinatarioDisparo,
  DisparoTemplateItem,
  PROVEDOR_LABEL,
  ProvedorComunicacao,
  acaoDoCanal,
} from "@/models/consignado/disparo.model";
import { CanalDraft } from "./disparo-draft";
import {
  contatoAutomaticoDoContexto,
  modosDoProvedor,
  rotuloContatoAutomatico,
  provedoresDoCanal,
  remetenteDoCatalogo,
  remetentesDoProvedor,
} from "./catalogo.helpers";
import CampoLabel from "./CampoLabel";
import TemplateMensagemForm from "./TemplateMensagemForm";
import TemplateEditorJanela from "./TemplateEditorJanela";
import { mensagemDoTemplate } from "./template-draft";
import TemplateReutilizavelCampo from "./TemplateReutilizavelCampo";
import { cardStyles, fieldFont } from "./styleTokens";

type Props = {
  canal: CanalDraft;
  contexto: ContextoDisparo;
  destinatario: DestinatarioDisparo;
  catalogo?: CatalogoDisparo;
  erro?: string;
  /** Canais já usados nos outros cards — um cadastro por canal. */
  canaisIndisponiveis: CanalComunicacao[];
  canaisDisponiveis: CanalComunicacao[];
  /**
   * Só vem quando há mais de um canal no disparo. O cadastro é de canal
   * único, mas o backend ainda aceita de 1 a 3: um registro criado pela API
   * com dois canais precisa de saída, senão abre com erro de validação e sem
   * como corrigir.
   */
  onRemove?: () => void;
  onChange: (patch: Partial<CanalDraft>) => void;
  onInserirToken: (token: string) => void;
};

const CANAL_ICON: Record<CanalComunicacao, typeof MdSms> = {
  [CanalComunicacao.EMAIL]: MdAlternateEmail,
  [CanalComunicacao.SMS]: MdSms,
  [CanalComunicacao.WHATSAPP]: MdChat,
};

/**
 * O catálogo já devolve o remetente pronto para exibição — no WhatsApp vem
 * "Nome (+55 11 …)", porque quem cadastra precisa ver de qual número a mensagem
 * sai, não um código interno. Este mapa cobre só o `PADRAO` de e-mail e SMS; o
 * resto passa direto, com o número.
 */
const REMETENTE_LABEL: Record<string, string> = {
  PADRAO: "Padrão",
};

/**
 * Um canal do disparo: por onde sai, por qual provedor, de qual remetente,
 * para qual contato e com qual mensagem.
 *
 * `acao` não aparece no formulário porque é sempre derivada do canal — o
 * backend recusa se as duas divergirem.
 */
export default function CanalCard({
  canal,
  contexto,
  destinatario,
  catalogo,
  erro,
  canaisIndisponiveis,
  canaisDisponiveis,
  onRemove,
  onChange,
  onInserirToken,
}: Props) {
  const provedores = provedoresDoCanal(catalogo, canal.canal);
  const remetentes = canal.provedor ? remetentesDoProvedor(catalogo, canal.canal, canal.provedor) : [];
  const [templateEmEdicao, setTemplateEmEdicao] = useState<DisparoTemplateItem | null>(null);
  const contato = contatoAutomaticoDoContexto(catalogo, contexto, destinatario, canal.canal);

  // Cadastro antigo gravou o código do remetente (`ESTEIRA`) e o catálogo hoje
  // publica "Esteira - 12997818585". Traduz uma vez, ao abrir, senão o select
  // fica vazio num disparo válido.
  const remetenteEquivalente = remetenteDoCatalogo(canal.remetente, remetentes);
  useEffect(() => {
    if (remetenteEquivalente && remetenteEquivalente !== canal.remetente) {
      onChange({ remetente: remetenteEquivalente });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remetenteEquivalente, canal.remetente]);

  const handleCanal = (novo: CanalComunicacao) => {
    // Trocar de canal invalida tudo o que dependia dele: provedor, remetente,
    // o modo do template e o template vinculado. O contato acompanha sozinho.
    const provedoresNovos = provedoresDoCanal(catalogo, novo);
    const provedor = provedoresNovos.length === 1 ? provedoresNovos[0] : null;
    const remetentesNovos = provedor ? remetentesDoProvedor(catalogo, novo, provedor) : [];
    const modos = provedor ? modosDoProvedor(catalogo, novo, provedor) : [];
    onChange({
      canal: novo,
      provedor,
      remetente: remetentesNovos.length === 1 ? remetentesNovos[0] : "",
      ...(modos.length ? { modo: modos[0] } : {}),
      assunto: "",
      conteudo: "",
      tipoConteudo: "TEXTO",
      identificador: "",
      idioma: "",
      produtoId: "",
      parametros: [],
      variaveis: [],
      templateId: undefined,
    });
  };

  const handleProvedor = (provedor: ProvedorComunicacao | "") => {
    if (!provedor) {
      onChange({ provedor: null, remetente: "", templateId: undefined });
      return;
    }
    const remetentesNovos = remetentesDoProvedor(catalogo, canal.canal, provedor);
    const modos = modosDoProvedor(catalogo, canal.canal, provedor);
    onChange({
      provedor,
      // O template é de um provedor só: trocar o provedor desfaz o vínculo e
      // o texto fica como mensagem própria.
      ...(provedor !== canal.provedor ? { templateId: undefined } : {}),
      remetente: remetentesNovos.includes(canal.remetente)
        ? canal.remetente
        : remetentesNovos.length === 1
          ? remetentesNovos[0]
          : "",
      ...(modos.length && !modos.includes(canal.modo) ? { modo: modos[0] } : {}),
    });
  };

  return (
    <Box {...cardStyles} p={{ base: 3, md: 4 }}>
      <Flex align="center" justify="space-between" mb={3}>
        <Flex align="center" gap={2}>
          <Icon as={CANAL_ICON[canal.canal]} boxSize={4} color="secondary" />
          <Text fontSize="sm" fontWeight="700" color="gray.800" {...fieldFont}>
            {CANAL_LABEL[canal.canal]}
          </Text>
          <Tooltip label="Ação derivada do canal — vai no cadastro automaticamente">
            <Tag size="sm" colorScheme="gray" {...fieldFont}>
              {acaoDoCanal(canal.canal)}
            </Tag>
          </Tooltip>
        </Flex>
      </Flex>

      {onRemove && (
        <Flex justify="flex-end" mt={-2} mb={1}>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="red"
            leftIcon={<DeleteIcon />}
            onClick={onRemove}
            {...fieldFont}
          >
            Remover canal
          </Button>
        </Flex>
      )}

      {erro && (
        <Flex align="center" gap={1.5} bg="red.50" borderRadius="md" px={3} py={2} mb={3}>
          <WarningTwoIcon boxSize={3} color="red.500" flexShrink={0} />
          <Text fontSize="xs" color="red.600" {...fieldFont}>
            {erro}
          </Text>
        </Flex>
      )}

      <VStack align="stretch" spacing={4}>
        <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
          <GridItem>
            <FormControl isRequired>
              <CampoLabel ajuda="Por onde a mensagem sai.">Canal</CampoLabel>
              <Select
                {...fieldFont}
                value={canal.canal}
                onChange={(e) => handleCanal(e.target.value as CanalComunicacao)}
              >
                {canaisDisponiveis.map((c) => (
                  <option key={c} value={c} disabled={c !== canal.canal && canaisIndisponiveis.includes(c)}>
                    {CANAL_LABEL[c]}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl isRequired>
              <CampoLabel ajuda="Quem entrega a mensagem.">Provedor</CampoLabel>
              <Select
                {...fieldFont}
                placeholder="Selecione"
                value={canal.provedor ?? ""}
                onChange={(e) => handleProvedor(e.target.value as ProvedorComunicacao | "")}
              >
                {provedores.map((p) => (
                  <option key={p} value={p}>
                    {PROVEDOR_LABEL[p]}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl isRequired>
              <CampoLabel ajuda="De qual dos nossos números a mensagem sai.">Remetente</CampoLabel>
              <Select
                {...fieldFont}
                placeholder="Selecione"
                value={canal.remetente}
                isDisabled={!canal.provedor}
                onChange={(e) => onChange({ remetente: e.target.value })}
              >
                {remetentes.map((r) => (
                  <option key={r} value={r}>
                    {REMETENTE_LABEL[r] ?? r}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>

          {/* O contato não se escolhe: sai do destinatário e do canal, decidido
              pelo backend (ver contatoAutomatico). Aqui só se mostra qual será. */}
          <GridItem>
            <FormControl>
              <CampoLabel ajuda="Automático, pelo destinatário e pelo canal: o tomador recebe no e-mail ou no telefone dele; o empregador, no e-mail cadastral ou no telefone do contato do RH.">
                Contato
              </CampoLabel>
              {contato ? (
                <Text fontSize="sm" color="gray.700" py={2} {...fieldFont}>
                  {contato.descricao}
                </Text>
              ) : (
                <Text fontSize="xs" color="orange.600" py={2} {...fieldFont}>
                  Este contexto não tem o {rotuloContatoAutomatico(destinatario, canal.canal)}.
                </Text>
              )}
            </FormControl>
          </GridItem>
        </Grid>

        <Divider />

        {canal.provedor && (
          <TemplateReutilizavelCampo
            canal={{ ...canal, provedor: canal.provedor }}
            contexto={contexto}
            catalogo={catalogo}
            onChange={onChange}
          />
        )}

        <TemplateMensagemForm
          canal={canal}
          catalogo={catalogo}
          contexto={contexto}
          onChange={onChange}
          onInserirToken={onInserirToken}
          somenteLeitura={!!canal.templateId}
          onEditarTemplate={setTemplateEmEdicao}
        />
        {templateEmEdicao && (
          <TemplateEditorJanela
            template={templateEmEdicao}
            catalogo={catalogo}
            isOpen
            onClose={() => setTemplateEmEdicao(null)}
            // O vínculo é vivo, mas o rascunho do disparo guarda a cópia de
            // quando o template foi escolhido: reaplica a mensagem salva.
            onSalvo={(t) => onChange(mensagemDoTemplate(t))}
          />
        )}
      </VStack>
    </Box>
  );
}
